// side-effect shims — keep as the first import so they run before any dependency code
import './polyfills/arrayWith'
import './polyfills/promiseWithResolvers'
import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { ManageLoader } from './components/ManageLoader'
import ErrorBoundary from './components/ErrorBoundary'
import { initOtel } from './otel'
import { initLogRocket } from './setupLogrocket'
import { createBrowserHistory } from 'history'
import { lazyLoadComponent } from 'lazyLoadComponent'
import { IS_TEST } from './constants/isttest'
import { GIT_SHA } from './constants/version'
import { APP_TYPE } from './constants/whereami'
import { installVisualViewportSettle } from './utils/visualViewportSettle'

const GlobalStyle = lazyLoadComponent(() => import('style/GlobalStyle'))
const Root = lazyLoadComponent(() => import('./Root'))

const browserHistory = createBrowserHistory({
  // override default window.alert user notification when a route change is blocked
  // use <RouterPrompt /> to display custom route change confirmation modal
  getUserConfirmation: () => {},
})

initOtel()
initLogRocket()

if (!IS_TEST) {
  console.log('config:', window.CONFIG)
  console.log('release:', GIT_SHA)
  console.log('platform:', APP_TYPE)
}

// default browser behavior is to inc/dec a numeric text field on scroll;
// we disable this behavior (MAUI-387).
document.addEventListener('wheel', (e) => {
  // @ts-ignore
  if (document.activeElement?.type !== 'number') return
  // @ts-ignore
  document.activeElement?.blur?.()
})

window.fakeScan = (str: string) => {
  const delay = 10 // milliseconds between keypresses

  str.split('').forEach((char, index) => {
    setTimeout(() => {
      const event = new KeyboardEvent('keydown', {
        key: char,
        code: `Key${char.toUpperCase()}`,
        keyCode: char.charCodeAt(0),
        which: char.charCodeAt(0),
        bubbles: true,
        cancelable: true,
      })
      document.dispatchEvent(event)
    }, index * delay)
  })

  // Send Enter key after all characters
  setTimeout(() => {
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
    })
    document.dispatchEvent(enterEvent)
  }, str.length * delay)
}

window.fakeSearch = (str: string) => {
  const input = document.querySelector(
    '#search-and-filter input[role="search"]'
  ) as HTMLInputElement | null
  if (!input) {
    console.warn('fakeSearch: no search bar found on this page')
    return
  }
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set
  setter?.call(input, str)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

window.togglePrintPreview = () => {
  const storageKey = 'printingPreview'
  const isPrintPreview = localStorage.getItem(storageKey) === 'true'
  localStorage.setItem(storageKey, `${!isPrintPreview}`)
  console.log(`togglePrintPreview is ${!isPrintPreview ? 'on' : 'off'}`)
}

// iOS fires visualViewport resize mid-keyboard-animation and may not
// fire again once it settles, leaving floating overlays sized for a viewport
// that no longer exists. Lives for the life of the document, so it is never
// torn down.
installVisualViewportSettle()

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(
  <>
    <React.Suspense fallback={null}>
      <GlobalStyle />
    </React.Suspense>
    <ErrorBoundary>
      <React.Suspense fallback={<ManageLoader loadingMessage="" />}>
        <Root history={browserHistory} />
      </React.Suspense>
    </ErrorBoundary>
  </>
)
